// feature/integrate-payment-element | September 10th 2025: Payment Modal Controller.
//  This controller manages the Payment Modal - the popup window that appears when users select a payment method.
import { Controller } from "stimulus"
import { getStripeElementOptions, getElementsOptions, getPaymentElementOptions } from "../utils/stripe_element_options"
import { paymentChallengeToken, resetPaymentChallenge, waitForPaymentChallenge } from "../utils/payment_security"

export default class extends Controller {
  static targets = ["methodName", "methodDescription", "loading", "confirmButton", "success", "successTitle", "successMessage", "continueButton", "container", "element", "errors", "paymentMethodInfo", "expressCheckoutElement"]
  static values = { 
    stripePublishableKey: String
  }

  connect() {
    this.paymentElementController = null
    this.currentPaymentMethodType = null
    this.currentPartnerId = null
    this.currentClientId = null
    this.currentSubscriptionId = null
    this.selectedElementPaymentMethodType = null
    this.elements = null
    this.stripe = null
    this.isConfirming = false
    this.cardObserver = null
    this.isPaymentMethodUpdate = false
    this.pendingProceedArgs = null
    
    // Define the escape handler
    this.escapeHandler = (event) => {
      if (event.key === 'Escape') {
          this.close();
      }
    };
    
    // Feature/payment-method-update | October 14th 2025: Listen for custom event to open modal from outside the controller
    // feature/hli-manage-donations: subscriptionId, when present, means this update targets one
    // specific client-scoped Subscription (not the donor's partner-wide payment method) -- see
    // initializeTraditionalPaymentElement/updatePaymentMethod, which branch on it to hit the
    // HLI-specific, subscription-scoped endpoints instead of the generic partner-wide ones.
    this.modalOpenHandler = (event) => {
      const { paymentMethodType, methodName, methodDescription, partnerId, partnerCurrency, clientId, subscriptionId, subscriptionUrl } = event.detail;
      this.isPaymentMethodUpdate = true;
      this.currentSubscriptionId = subscriptionId;
      // The tab's own subscription-scoped endpoints (HLI's, or OFTW's Donation Flow pledges') --
      // see subscriptionEndpoint.
      this.currentSubscriptionUrl = subscriptionUrl;
      this.show(paymentMethodType, partnerId, methodName, methodDescription, null, partnerCurrency, clientId);
    };
    
    document.addEventListener('keydown', this.escapeHandler);
    document.addEventListener('payment-method:open-modal', this.modalOpenHandler);
    
    // Feature/payment-method-update | October 14th 2025: Hide Apple Pay/Google Pay button if not supported on this device
    this.checkDigitalWalletSupport();
  }
  
  // Donation Flow sets data-form-scope="donation_form"; defaults to pledge_form otherwise.
  formScope() {
    const wizardRoot = document.querySelector('[data-wizard-root]');
    return wizardRoot?.dataset.formScope || 'pledge_form';
  }

  fieldSelector(name) {
    return `[name="${this.formScope()}[${name}]"]`;
  }

  // currency_code rides along so a redirect-back knows which Stripe account the PaymentIntent
  // was created under (partner_id alone doesn't encode currency for a client like HLI that uses
  // one Partner with a currency selector, unlike OFTW's per-currency Partner rows).
  buildReturnUrl() {
    const url = new URL(window.location.href);
    if (this.currentPartnerCurrency) url.searchParams.set('currency_code', this.currentPartnerCurrency);
    return url.toString();
  }

// Snapshot the pledge form for redirect
  pledgeFormSnapshotForRedirect() {
    const wizardRoot = document.querySelector('[data-wizard-root]');
    const form = wizardRoot?.tagName === 'FORM' ? wizardRoot : wizardRoot?.querySelector('form');
    if (!form) return {};

    const scope = this.formScope();
    const snapshot = {};
    new FormData(form).forEach((value, key) => {
      const match = key.match(new RegExp(`^${scope}\\[(\\w+)\\]$`));
      if (match && typeof value === 'string') snapshot[match[1]] = value;
    });
    return snapshot;
  }

  disconnect() {
    // Clean up event listeners
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
    if (this.modalOpenHandler) {
      document.removeEventListener('payment-method:open-modal', this.modalOpenHandler);
    }
  }

  // Feature/payment-method-update | October 14th 2025: Check if digital wallets are supported on this device
  async checkDigitalWalletSupport() {

    // TODO: After removing the oftw-wizard controller, remove this check.
    if (document.querySelector('[data-controller*="oftw-wizard"]')) return;

    const digitalWalletsButtons = document.querySelectorAll('[data-payment-method-type="digital_wallets"]');
    if (!digitalWalletsButtons.length) return;

    try {
      const stripeKey = this.stripePublishableKeyValue;
      if (!stripeKey) return;
      
      const stripe = Stripe(stripeKey);
      const paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: { label: 'Test', amount: 1000 },
        requestPayerName: true,
        requestPayerEmail: true,
      });
      
      const canMakePayment = await paymentRequest.canMakePayment();
      if (!canMakePayment) {
        digitalWalletsButtons.forEach((button) => {
          const cardElement = button.closest('.control');
          (cardElement || button).style.display = 'none';
        });
      }
    } catch (error) {
      // Keep button visible on error
      console.log('Digital wallet check failed, keeping button visible:', error);
    }
  }

  // clientId: Donation Flow has no Partner to key off
  // A subscription-scoped endpoint (payment_method / payment_method_setup_intent) on the opening
  // tab's own controller -- HLI's when the page passed no subscriptionUrl.
  subscriptionEndpoint(action) {
    const base = this.currentSubscriptionUrl || `/hli_donation_subscriptions/${this.currentSubscriptionId}`
    return `${base}/${action}`
  }

  show(paymentMethodType, partnerId, methodName, methodDescription, stripeAccountId, partnerCurrency, clientId) {

    this.currentPaymentMethodType = paymentMethodType
    this.currentPartnerId = partnerId
    this.currentClientId = clientId
    this.currentStripeAccountId = stripeAccountId
    this.currentPartnerCurrency = partnerCurrency
    this.clearError()
    this.pendingProceedArgs = null
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.classList.add('is-hidden')
    }

    // Update modal content
    this.methodNameTarget.textContent = methodName
    this.methodDescriptionTarget.textContent = methodDescription
    
    // Show the modal
    this.element.classList.add('is-active')
    
    // Initialize Payment Element after a short delay to ensure modal is visible
    setTimeout(() => {
      this.initializePaymentElement()
    }, 100)
  }

  // feature/integrate-payment-element | September 10th 2025: Close the payment modal.
  close() {
    
    try {
      this.element.classList.remove('is-active')
      
      // Clean up Payment Element
      if (this.paymentElement) {
        this.paymentElement.unmount()
        this.paymentElement = null
      }
      
      
      // Clean up card hiding observer
      if (this.cardObserver) {
        this.cardObserver.disconnect()
        this.cardObserver = null
      }
      
      // Remove card hiding CSS
      const cardHidingStyles = document.getElementById('digital-wallet-card-hiding-styles');
      if (cardHidingStyles) {
        cardHidingStyles.remove();
      }
      
      // Remove digital wallet only styles
      const digitalWalletStyles = document.getElementById('digital-wallet-only-styles');
      if (digitalWalletStyles) {
        digitalWalletStyles.remove();
      }
      
      // Clean up Payment Request API elements
      if (this.prButton) {
        this.prButton.unmount();
        this.prButton = null;
      }
      const prContainer = document.getElementById('payment-request-container');
      if (prContainer) {
        prContainer.remove();
      }

      // feature/hli-manage-donations: clear the Express Checkout Element so reopening the modal
      // doesn't mount a second one alongside a stale, unmounted first instance.
      if (this.hasExpressCheckoutElementTarget) {
        this.expressCheckoutElementTarget.innerHTML = ''
        this.expressCheckoutElementTarget.classList.add('is-invisible')
      }

      
      // Reset UI state
      this.clearError()
      this.loadingTarget.classList.add('is-hidden')
      this.successTarget.classList.add('is-hidden')
      if (this.containerTarget) {
        this.containerTarget.classList.add('is-hidden')
      }
      if (this.paymentMethodInfoTarget) {
        this.paymentMethodInfoTarget.classList.remove('is-hidden')
      }
      this.confirmButtonTarget.style.display = 'block'
      this.confirmButtonTarget.disabled = false
      this.confirmButtonTarget.textContent = 'Connect' 
      
      
      // Reset state
      this.currentPaymentMethodType = null
      this.currentPartnerId = null
      this.currentClientId = null
      this.currentSubscriptionId = null
      this.selectedElementPaymentMethodType = null
      this.currentStripeAccountId = null
      this.currentPartnerCurrency = null
      this.elements = null
      this.clientSecret = null
      this.isConfirming = false
      // PR #710 review (liz-durang): closing the modal (Escape/click-outside) while sitting on the
      // micro-deposit pending-verification screen left pendingProceedArgs set from that session, so
      // clear it here too rather than only when Continue is clicked.
      this.pendingProceedArgs = null
      if (this.hasContinueButtonTarget) {
        this.continueButtonTarget.classList.add('is-hidden')
      }
    } catch (error) {
      // Continue with cleanup even if there's a display error (likely browser extension conflict)
    }
  }

  async confirmPayment(event) {
    // Feature/payment-method-update | October 14th 2025: Prevent modal from closing before validation
    if (event) {
      event.stopImmediatePropagation();
    }    

    // Use standard Payment Element for all payment methods
    if (!this.paymentElement || !this.clientSecret) {
      return
    }

    // Prevent multiple confirmation attempts
    if (this.isConfirming) {
      return;
    }

    this.clearError();

    // Feature/payment-method-update | October 14th 2025: Validate Payment Element BEFORE setting isConfirming
    try {
      const submitResult = await this.elements.submit();
      if (submitResult && (submitResult.code || submitResult.error)) {
        const errorMessage = submitResult.message || submitResult.error?.message || "Please complete all required fields.";
        console.error('Payment Element validation error:', submitResult);
        this.showError(errorMessage);
        if (event) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }

        // Ensure modal stays active
        if (!this.element.classList.contains('is-active')) {
          this.element.classList.add('is-active');
        }

        this.isConfirming = false;
        return;
      }
    } catch (validationError) {
      console.error('Error validating Payment Element:', validationError);
      this.showError("Something went wrong validating your payment details. Please try again.");
      if (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      // Ensure modal stays active
      if (!this.element.classList.contains('is-active')) {
        this.element.classList.add('is-active');
      }

      // Stop all form processing
      this.isConfirming = false;
      return;
    }
    
    this.isConfirming = true;
    // Show loading state
    this.showLoading(true)
    
    try {
      let result;

      // Payment-method collection never moves money. Stripe creates the real
      // PaymentIntent later, when a contribution is actually due.
      result = await this.stripe.confirmSetup({
        elements: this.elements,
        redirect: 'if_required'
      });

      if (result.error) {
        console.error('Payment confirmation error:', result.error);
        this.showError(result.error.message || "We couldn't connect your payment method. Please try again.");
        this.showLoading(false);
        this.isConfirming = false;
        return;
      }

      // feature/hli-manage-donations: 'full' mode lets the donor pick Card or Bank account
      // inside the same Element, so which one they actually chose isn't knowable from the
      // static currentPaymentMethodType the way it is for every other (single-type) trigger --
      // selectedElementPaymentMethodType (set by the Payment Element's own 'change' event, see
      // initializeTraditionalPaymentElement) is Stripe's real type string already, so it's used
      // directly instead of going through getActualStripePaymentMethodType's button->Stripe-type
      // mapping.
      const actualPaymentMethodType = this.selectedElementPaymentMethodType
        || this.getActualStripePaymentMethodType(this.currentPaymentMethodType);

      const pendingMicrodepositVerification =
        result.setupIntent.status === 'requires_action' && actualPaymentMethodType === 'us_bank_account';

      if (result.setupIntent.status === 'succeeded' || (pendingMicrodepositVerification && result.setupIntent.payment_method)) {
        const paymentMethodId = result.setupIntent.payment_method;

        // Store the payment method information
        this.storePaymentMethodInfo(paymentMethodId, actualPaymentMethodType);

        if (pendingMicrodepositVerification) {
          // us_bank_account uses verification_method: 'automatic' (see PaymentMethodOptionsService), so Stripe
          // falls back to micro-deposit verification when the linked institution doesn't support instant
          // verification via Financial Connections - a real 1-2 business day out-of-band flow, not a failure.
          // Stripe attaches the payment method to the SetupIntent as soon as it's linked, verified or not, so
          // we don't make the donor come back later - we proceed now with the pledge and let verification
          // finish in the background before the first charge is actually attempted. Stripe emails the donor
          // a link to confirm the deposit amounts once they land, so tell them to expect it.
          console.log('Bank account linked, pending micro-deposit verification:', result.setupIntent.status);
          this.showSuccess(
            "Bank Account Linked!",
            "We've sent a couple of small deposits to verify your account - this can take 1-2 business days. Once they arrive, check your email for a link from Stripe to confirm the amounts and finish verifying your account."
          );
          // This message needs to actually be read (unlike the instant-success case below, which
          // proceeds immediately) - wait for the donor to click Continue rather than auto-advancing
          // the wizard out from under them.
          this.pendingProceedArgs = { actualPaymentMethodType, paymentMethodId };
          this.continueButtonTarget.classList.remove('is-hidden');
        } else {
          this.showSuccess()
          this.notifyWizardAndProceed(actualPaymentMethodType, paymentMethodId);
        }
      } else if (pendingMicrodepositVerification) {
        // Defensive fallback: Stripe docs say payment_method should be present whenever a SetupIntent
        // requires micro-deposit verification, but if it's ever missing we can't proceed without it.
        console.error('Bank account requires micro-deposit verification but no payment_method was returned:', {
          setupIntentId: result.setupIntent.id,
          setupIntentStatus: result.setupIntent.status,
          paymentMethod: result.setupIntent.payment_method,
          paymentMethodType: this.currentPaymentMethodType,
          partnerId: this.currentPartnerId
        });
        this.showError("Your bank account was linked, but we couldn't finish setting it up. Please try again.");
        this.showLoading(false);
        this.isConfirming = false;
      } else {
        console.error('Payment confirmation failed with status:', result.setupIntent.status);
        this.showError("We couldn't connect your payment method. Please try again.");
        this.showLoading(false);
        this.isConfirming = false;
      }
    } catch (error) {
      console.error('Error confirming payment:', error)
      this.showError("Something went wrong connecting your payment method. Please try again.");
      this.showLoading(false)
      this.isConfirming = false;
    }
  }

  async initializePaymentElement() {
    try {
      if (!this.currentPaymentMethodType || (!this.currentPartnerId && !this.currentClientId && !this.currentSubscriptionId)) {
        console.error('Payment Modal: Missing required parameters', {
          paymentMethodType: this.currentPaymentMethodType,
          partnerId: this.currentPartnerId,
          clientId: this.currentClientId,
          subscriptionId: this.currentSubscriptionId
        });
        return;
      }

      // Use Payment Element for all payment methods, including digital wallets
      await this.initializeTraditionalPaymentElement();
    } catch (error) {
      console.error('Error initializing Payment Element:', error);
    }
  }

  showLoading(show) {
    if (show) {
      this.loadingTarget.classList.remove('is-hidden')
      this.confirmButtonTarget.disabled = true
      this.confirmButtonTarget.textContent = 'Processing...'
    } else {
      this.loadingTarget.classList.add('is-hidden')
      this.confirmButtonTarget.disabled = false
      this.confirmButtonTarget.textContent = 'Connect'
    }
  }

  getElementOptions(paymentMethodType) {
    return getStripeElementOptions(paymentMethodType);
  }

  // Surface a Payment Element/confirmation error to the user. Without this, failures were only
  // logged to the console - the button would silently reset with no visible feedback at all.
  showError(message) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.textContent = message
  }

  clearError() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.textContent = ''
  }

  showSuccess(title, message) {
    try {
      // Hide loading, payment element, and payment method info
      this.loadingTarget.classList.add('is-hidden')
      if (this.containerTarget) {
        this.containerTarget.classList.add('is-hidden')
      }
      if (this.paymentMethodInfoTarget) {
        this.paymentMethodInfoTarget.classList.add('is-hidden')
      }

      // Allow callers (e.g. a bank account pending micro-deposit verification) to override the
      // default copy - it's genuinely connected, but not fully verified yet, so it shouldn't
      // claim to be "successfully set up" without qualification.
      if (title && this.hasSuccessTitleTarget) {
        this.successTitleTarget.textContent = title
      }
      if (message && this.hasSuccessMessageTarget) {
        this.successMessageTarget.textContent = message
      }

      // Show success state
      this.successTarget.classList.remove('is-hidden')

      // Hide the confirm button
      this.confirmButtonTarget.style.display = 'none'
    } catch (error) {
      // Continue with the flow even if there's a display error (likely browser extension conflict)
    }
  }

  storePaymentMethodInfo(paymentMethodId, paymentMethodType) {
    // Set the hidden form fields with payment method data
    const paymentMethodIdField = document.querySelector(this.fieldSelector('payment_method_id'));
    const processorMethodIdField = document.querySelector(this.fieldSelector('payment_processor_payment_method_id'));
    const processorMethodTypeField = document.querySelector(this.fieldSelector('payment_processor_payment_method_type'));
    
    if (paymentMethodIdField) {
      paymentMethodIdField.value = this.currentPaymentMethodType;
    }
    
    if (processorMethodIdField) {
      processorMethodIdField.value = paymentMethodId;
    }
    
    if (processorMethodTypeField) {
      processorMethodTypeField.value = paymentMethodType;
    }
    
    // Mark the payment method as selected in the UI
    this.markPaymentMethodAsSelected();
  }

  markPaymentMethodAsSelected() {
    // Remove any existing selected state from payment method buttons
    const allButtons = document.querySelectorAll('.payment-method-button');
    allButtons.forEach(button => {
      button.classList.remove('is-selected');
      button.style.border = '';
    });
    
    // Mark the selected button based on current payment method type
    const selectedButton = document.querySelector(`[data-payment-method-type="${this.currentPaymentMethodType}"]`);
    if (selectedButton) {
      selectedButton.classList.add('is-selected');
      selectedButton.style.border = '2px solid #2675F8';
    }
  }
  
  // Continue button on the micro-deposit pending-verification success screen - deferred until the
  // donor clicks it (rather than auto-proceeding) so they actually have time to read that message.
  proceedAfterMicrodepositSuccess() {
    if (!this.pendingProceedArgs) {
      // PR #710 review (liz-durang): shouldn't happen since the Continue button is only shown
      // alongside pendingProceedArgs being set, but warn rather than silently no-op if it ever does.
      console.warn('proceedAfterMicrodepositSuccess called with no pendingProceedArgs')
      return
    }

    const { actualPaymentMethodType, paymentMethodId } = this.pendingProceedArgs
    this.pendingProceedArgs = null
    this.notifyWizardAndProceed(actualPaymentMethodType, paymentMethodId)
  }

  notifyWizardAndProceed(actualStripeType, paymentMethodId) {
    // Feature/payment-method-update | October 14th 2025: Check if this is a payment method update not pledge flow
    if (this.isPaymentMethodUpdate) {
      this.updatePaymentMethod(paymentMethodId, actualStripeType)
      return
    }
    
    // Get wizard controller directly and call its method
    const wizardElement = document.querySelector('[data-controller*="oftw-wizard"]');
    if (wizardElement) {
      const wizardController = this.application.getControllerForElementAndIdentifier(
        wizardElement, 
        'oftw-wizard'
      );
      
      if (wizardController && wizardController.setPaymentMethodData) {
        // Call the wizard's method directly with all three parameters
        wizardController.setPaymentMethodData(
          this.currentPaymentMethodType,  // button type
          paymentMethodId,                 // payment method ID
          actualStripeType                 // Stripe payment method type
        );
        
        // Mark as selected and show connected state
        if (wizardController.markPaymentMethodAsSelected) {
          wizardController.markPaymentMethodAsSelected(this.currentPaymentMethodType);
        }
        
        if (wizardController.showConnectedPaymentMethodState) {
          wizardController.showConnectedPaymentMethodState();
        }
      }
    }
    
    // Dispatch event as backup for event listeners
    const event = new CustomEvent('payment-modal:paymentCompleted', {
      detail: {
        buttonType: this.currentPaymentMethodType,
        paymentMethodId: paymentMethodId,
        stripePaymentMethodType: actualStripeType
      },
      bubbles: true
    });
    
    // Dispatch on the wizard root
    const wizardRoot = document.querySelector('[data-wizard-root]');
    if (wizardRoot) {
      wizardRoot.dispatchEvent(event);
    }
    
    // Wait a moment to show success, then proceed to step 4
    setTimeout(() => {
      this.proceedToNextStep()
    }, 1500)
  }

  // Feature/payment-method-update | October 14th 2025: Method to handle payment method updates
  async updatePaymentMethod(paymentMethodId, actualStripeType) {
    try {
      // Create a form to submit the payment method update
      const form = document.createElement('form')
      form.method = 'POST'
      // feature/hli-manage-donations: a subscription-scoped update saves the new payment method
      // onto that one Subscription only -- see HliDonations::SubscriptionsController#update_payment_method.
      // Reusing PaymentMethodsController#create here would be wrong: it only ever reassigns the
      // donor's Partner-affiliated subscriptions, deliberately leaving a client-scoped (HLI)
      // subscription's payment_method_id untouched.
      form.action = this.currentSubscriptionId
        ? this.subscriptionEndpoint('payment_method')
        : '/payment_methods'

      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)

      // Add payment method ID
      const paymentMethodInput = document.createElement('input')
      paymentMethodInput.type = 'hidden'
      paymentMethodInput.name = 'payment_method_id'
      paymentMethodInput.value = paymentMethodId
      form.appendChild(paymentMethodInput)

      // Add customer ID if available (not applicable to the subscription-scoped endpoint, which
      // resolves everything from the subscription itself)
      if (this.currentPartnerId && !this.currentSubscriptionId) {
        const customerInput = document.createElement('input')
        customerInput.type = 'hidden'
        customerInput.name = 'customer_id'
        customerInput.value = this.currentPartnerId
        form.appendChild(customerInput)
      }

      // Submit the form
      document.body.appendChild(form)
      form.submit()
      
    } catch (error) {
      console.error('Error updating payment method:', error)
      this.showLoading(false)
      this.isConfirming = false
    }
  }
  
  // Map our internal payment method type codes to actual Stripe payment method types
  getActualStripePaymentMethodType(internalType) {
    switch(internalType) {
      case 'card':
        return 'card';
      case 'digital_wallets':
        // Apple Pay and Google Pay use Stripe's "card" type
        return 'card';
      case 'us_bank_account':
        return 'us_bank_account';
      case 'bacs_debit':
        return 'bacs_debit';
      case 'acss_debit':
        return 'acss_debit';
      case 'bank_account':
        // Generic bank account - should be mapped by currency on server
        return 'us_bank_account';
      default:
        console.warn('Unknown payment method type:', internalType, '- defaulting to card');
        return 'card';
    }
  }

  proceedToNextStep() {
    // Close the modal
    this.close()
    
    // Find the wizard controller and proceed directly to step 4 (index 3)
    const wizardElement = document.querySelector('[data-controller*="oftw-wizard"]');
    const wizardController = this.application.getControllerForElementAndIdentifier(
      wizardElement, 
      'oftw-wizard'
    )
    
    if (wizardController) {
      // Go directly to step 4 (index 3) - the personal details step
      wizardController.showStep(3)
    }
  }

  async initializeTraditionalPaymentElement() {
    
    // Feature/integrate-payment-element | October 14th 2025: Reset references to avoid conflicts when switching between payment methods
    this.paymentElement = null;
    this.elements = null;
    this.clientSecret = null;
    
    // Show traditional payment element
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove('is-hidden');
    }
    
    // Continue with the existing payment element initialization logic
    // feature/hli-manage-donations: a subscription-scoped update hits its own endpoint, which
    // resolves the Stripe account from the (server-verified, donor-owned) subscription itself
    // rather than trusting a submitted partner_id/client_id -- see
    // HliDonations::SubscriptionsController#payment_method_setup_intent.
    const setupIntentUrl = this.currentSubscriptionId
      ? this.subscriptionEndpoint('payment_method_setup_intent')
      : '/get_payment_element_setup_intent'
    try {
      await waitForPaymentChallenge()
    } catch (err) {
      this.showError(err.message || 'Please complete the security check and try again.')
      return
    }
    const setupIntentBody = this.currentSubscriptionId
      ? { payment_challenge_token: paymentChallengeToken() }
      : {
          payment_method_type: this.currentPaymentMethodType,
          partner_id: this.currentPartnerId,
          currency_code: this.currentPartnerCurrency,
          client_id: this.currentClientId,
          payment_challenge_token: paymentChallengeToken(),
          return_url: this.buildReturnUrl(),
          pledge_form_snapshot: this.pledgeFormSnapshotForRedirect()
        }
    const response = await fetch(setupIntentUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(setupIntentBody)
    });
    resetPaymentChallenge();

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        return;
      }

      const { client_secret, error, redirect_to_checkout, stripe_account_id, use_platform_account } = responseData;

      if (redirect_to_checkout) {
        window.location.assign(redirect_to_checkout);
        return;
      }

      if (error) {
        this.showError(error);
        return;
      }

      if (!response.ok) {
        this.showError('Could not start payment setup. Please try again.');
        return;
      }

      if (!client_secret) {
        console.error('No client secret received');
        return;
      }

      // Initialize Stripe with the correct account
      const stripeOptions = use_platform_account ? {} : { stripeAccount: stripe_account_id };
      this.stripe = Stripe(this.stripePublishableKeyValue, stripeOptions);

      // Create payment element with proper options for the payment method type
      const elementOptions = this.getElementOptions(this.currentPaymentMethodType);

      // Create elements with client secret and appearance options only
      // Don't pass paymentMethodTypes, fields, wallets, or layout as they're not recognized in elements()
      const elementsOptions = getElementsOptions(elementOptions);
      
      const elements = this.stripe.elements({
        clientSecret: client_secret,
        ...elementsOptions
      });

      // Create payment element with payment method specific options
      // Note: paymentMethodTypes should not be passed to elements.create('payment')
      // as it's configured on the Setup Intent server-side
      const paymentElementOptions = getPaymentElementOptions(elementOptions);
      
      const paymentElement = elements.create('payment', paymentElementOptions);

      // Mount the element
      if (!this.elementTarget) {
        console.error('Payment element target not found');
        return;
      }

      paymentElement.mount(this.elementTarget);

      // feature/hli-manage-donations: 'full' mode's tabbed card/bank Element lets the donor
      // switch types after mount -- track whichever one they're actually on so confirmPayment()
      // doesn't have to guess from the static currentPaymentMethodType.
      if (this.currentPaymentMethodType === 'full') {
        paymentElement.on('change', (event) => {
          this.selectedElementPaymentMethodType = event.value?.type || null
        })
      }

      // Store references for later use BEFORE calling initializePaymentRequestAPI
      this.paymentElement = paymentElement;
      this.elements = elements;
      this.clientSecret = client_secret;

      // Show the payment element container
      if (this.containerTarget) {
        this.containerTarget.classList.remove('is-hidden');
      }

      // For digital wallets, use Payment Request API for wallet-only experience
      if (this.currentPaymentMethodType === 'digital_wallets') {
        this.initializePaymentRequestAPI();
      }

      // feature/hli-manage-donations: 'full' pairs the tabbed card/bank Payment Element above
      // with an Express Checkout Element for Apple Pay/Google Pay/Link, matching the donation
      // flow's Review step rather than the older Payment Request API branch above.
      if (this.currentPaymentMethodType === 'full') {
        this.initializeExpressCheckoutElement();
      }

  }

  // feature/hli-manage-donations: mirrors one_time_payment_controller.js#setupPaymentElement's
  // Express Checkout Element setup, adapted to confirm a SetupIntent (via the existing
  // confirmPayment() method, which already calls stripe.confirmSetup) instead of a PaymentIntent.
  initializeExpressCheckoutElement() {
    if (!this.hasExpressCheckoutElementTarget || !this.elements) return

    const expressCheckoutElement = this.elements.create('expressCheckout', {
      paymentMethods: {
        googlePay: 'always', applePay: 'always', link: 'auto',
        paypal: 'never', klarna: 'never', amazonPay: 'never'
      },
      buttonType: { googlePay: 'plain', applePay: 'plain' }
    })

    this.expressCheckoutElementTarget.classList.add('is-invisible')
    this.expressCheckoutElementTarget.innerHTML = ''
    expressCheckoutElement.mount(this.expressCheckoutElementTarget)
    expressCheckoutElement.on('confirm', () => this.confirmPayment())
    expressCheckoutElement.on('availablepaymentmethodschange', ({ paymentMethods }) => {
      this.expressCheckoutElementTarget.classList.toggle('is-invisible', !paymentMethods)
    })
  }

  async initializePaymentRequestAPI() {
    try {
      
      // Hide the regular payment element
      if (this.containerTarget) {
        this.containerTarget.classList.add('is-hidden');
      }
      
      // Create a container for the payment request button
      let prContainer = document.getElementById('payment-request-container');
      if (!prContainer) {
        prContainer = document.createElement('div');
        prContainer.id = 'payment-request-container';
        prContainer.style.cssText = 'margin: 20px 0; text-align: center;';
        
        // Insert it before the regular payment element container
        if (this.containerTarget) {
          this.containerTarget.parentNode.insertBefore(prContainer, this.containerTarget);
        }
      }
      
      // Without a real amount the Apple Pay sheet would show a made-up figure -- fall back to the
      // regular payment element instead of offering the wallet.
      const amount = this.getCurrentAmount();
      if (amount === null) {
        console.warn('payment-modal: no usable donation amount on the form; skipping the Apple Pay button');
        if (this.containerTarget) this.containerTarget.classList.remove('is-hidden');
        return;
      }

      const paymentRequest = this.stripe.paymentRequest({
        country: 'US',
        currency: (this.currentPartnerCurrency || 'usd').toLowerCase(),
        total: {
          label: 'Donation',
          amount: amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });
      
      // Create payment request button using the existing elements instance
      const prButton = this.elements.create('paymentRequestButton', {
        paymentRequest: paymentRequest,
        style: {
          paymentRequestButton: {
            type: 'default',
            theme: 'dark',
            height: '48px',
          },
        },
      });
      
      // Check if payment request is available
      const canMakePayment = await paymentRequest.canMakePayment();
      
      if (canMakePayment) {
        prButton.mount(prContainer);
        
        // Handle payment method selection
        paymentRequest.on('paymentmethod', async (ev) => {

          try {
            // Confirm the payment method with the setup intent
            const { error, setupIntent } = await this.stripe.confirmCardSetup(
              this.clientSecret,
              {
                payment_method: ev.paymentMethod.id
              }
            );

            if (error) {
              console.error('Payment confirmation error:', error);
              ev.complete('fail');
              this.showError(error.message || "We couldn't connect your payment method. Please try again.");
            } else {
              ev.complete('success');
              
              const paymentMethodId = setupIntent.payment_method;
              
              // Map our internal type to actual Stripe type
              // Digital wallets (Apple Pay/Google Pay) use Stripe's "card" type
              const actualPaymentMethodType = this.getActualStripePaymentMethodType(this.currentPaymentMethodType);
              
              // Store the payment method information
              this.storePaymentMethodInfo(paymentMethodId, actualPaymentMethodType);
              this.showSuccess();
              
              // Notify wizard and proceed (consistent with card flow)
              this.notifyWizardAndProceed(actualPaymentMethodType, paymentMethodId);
            }
          } catch (error) {
            console.error('Error confirming payment:', error);
            ev.complete('fail');
            this.showError("Something went wrong connecting your payment method. Please try again.");
          }
        });
        
        this.paymentRequest = paymentRequest;
        this.prButton = prButton;
        
      } else {
        this.showPaymentRequestFallback(prContainer);
      }
      
    } catch (error) {
      console.error('Error initializing Payment Request API:', error);
    }
  }
  
  
  showPaymentRequestFallback(container) {
    container.innerHTML = `
      <div class="notification is-warning">
        <div class="content">
          <p><strong>Digital wallets not available</strong></p>
          <p>Apple Pay and Google Pay are not available on this device. Please use a different payment method.</p>
        </div>
      </div>
    `;
  }

  // Amount (in cents) for the Payment Request API / Apple Pay sheet. Each flow's wizard-root adapter
  // (payment-connection for the Donation Flow, oftw-wizard for the pledge) owns what the donor is
  // actually charged -- some add a tip, some don't -- and exposes it as currentAmountCents(). Returns
  // null when no adapter can supply a usable figure, so the caller skips the wallet button rather
  // than showing a made-up amount.
  getCurrentAmount() {
    const root = document.querySelector('[data-wizard-root]');
    const adapter = root && (root.dataset.controller || '').split(' ')
      .map((identifier) => this.application.getControllerForElementAndIdentifier(root, identifier))
      .find((controller) => typeof controller?.currentAmountCents === 'function');

    const total = adapter?.currentAmountCents() ?? 0;
    return total >= 50 && total <= 99999999 ? total : null;
  }

}
